import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

const DropdownCustom = ({ 
  // Main props
  options = [],           // Array of options to display
  selectedValue = '',     // Currently selected value
  placeholder = 'Select', // Default text when nothing is selected
  width,                  // Dropdown width (Tailwind classes)
  
  // Callbacks
  onSelect = () => {},   // Function called when an option is selected
  onToggle = () => {},   // Function called when dropdown opens/closes
  
  // Styling
  buttonClassName = '',  // Additional classes for the button
  dropdownClassName = '', // Additional classes for the dropdown
  optionClassName = '',  // Additional classes for the options
  
  // Behavior
  disabled = false,      // Whether the dropdown is disabled
  showAllOption = false, // Whether to show "All" option at the top
  allOption = null,      // All option as object { value, label }
  
  // Positioning
  position = 'right',    // 'left', 'right', 'center'
  zIndex = 'z-10',        // Z-index of the dropdown
  showRadio = false,
  usePortal = false,
  portalRef = null,
  ...props
}) => {
  // ###### STATES
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Get label for selected value
  let selectedVal = typeof selectedValue === 'object' && selectedValue !== null ? selectedValue.value : selectedValue;
  let selectedLabel = options && selectedVal
    ? (options.find(option => (typeof option === 'object' ? option.value : option) === selectedVal)?.label
        || (typeof selectedValue === 'object' && selectedValue !== null ? selectedValue.label : selectedVal))
    : placeholder;

  // ###### EFFECTS
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, usePortal]);

  // Função para calcular estilo do Portal
  const getPortalStyle = () => {
    const ref = portalRef && portalRef.current ? portalRef : buttonRef;
    if (!ref.current) return {};
    const rect = ref.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 20,
      minWidth: rect.width,
    };
  };

  // Ajuda na posição do menu e click fora (apenas se usar portal)
  useEffect(() => {
    if (!usePortal || !isOpen) return;
    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [usePortal, isOpen]);

  // ###### HANDLERS
  const handleToggle = () => {
    if (disabled) return;
    
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle(newState);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  // ###### POSITIONING
  const getPositionClasses = () => {
    switch (position) {
      case 'left':
        return 'left-0';
      case 'center':
        return 'left-1/2 transform -translate-x-1/2';
      case 'right':
      default:
        return 'right-0';
    }
  };

  // ###### RENDER
  return (
    <div className="relative" ref={buttonRef}>
      {/* Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          inline-flex justify-between items-center ${width || 'w-full'} rounded-md border 
          bg-white border-gray-300 px-2 py-1.5 text-xs text-gray-700 
          hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-1 
          focus:ring-blue-500 focus:border-blue-500 transition-all
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}
          ${buttonClassName}
        `}
      >
        <span className="truncate">
          {selectedLabel}
        </span>
        <ChevronDownIcon 
          className={`ml-1 h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        usePortal
          ? createPortal(
              <div style={getPortalStyle()}>
                <div
                  ref={dropdownRef}
                  className={`rounded-md bg-white shadow-lg border border-gray-200 max-h-60 overflow-y-auto ${zIndex} ${width || 'w-full'} ${dropdownClassName}`}
                >
                  {/* All Option (if enabled) */}
                  {showAllOption && allOption && (
                    <div
                      onClick={() => handleSelect(allOption)}
                      className={`
                        cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                        ${selectedValue === allOption.value ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                        ${optionClassName}
                        ${showRadio ? 'flex items-center' : ''}
                      `}
                    >
                      {showRadio && (
                        <input
                          type="radio"
                          checked={selectedValue === allOption.value}
                          readOnly
                          className="mr-2 accent-green-400"
                        />
                      )}
                      {allOption.label}
                    </div>
                  )}
                  {/* Options */}
                  {options.map((option, index) => {
                    const optionValue = typeof option === 'object' ? option.value : option;
                    const optionLabel = typeof option === 'object' ? option.label : option;
                    const isSelected = selectedValue === optionValue;
                    return (
                      <div
                        key={index}
                        onClick={() => handleSelect(option)}
                        className={`
                          cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                          ${isSelected ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                          ${optionClassName}
                          ${showRadio ? 'flex items-center' : ''}
                        `}
                      >
                        {showRadio && (
                          <input
                            type="radio"
                            checked={isSelected}
                            readOnly
                            className="mr-2 accent-green-400"
                          />
                        )}
                        {optionLabel}
                      </div>
                    );
                  })}
                  {/* Empty State */}
                  {options.length === 0 && (
                    <div className="px-3 py-1.5 text-xs text-gray-500 italic">
                      No options available
                    </div>
                  )}
                </div>
              </div>,
              document.body
            )
          : (
            <div
              ref={dropdownRef}
              className={`absolute top-full ${getPositionClasses()} ${zIndex} mt-1 ${width || 'w-full'} rounded-md bg-white shadow-lg border border-gray-200 max-h-60 overflow-y-auto ${dropdownClassName}`}
            >
              {/* All Option (if enabled) */}
              {showAllOption && allOption && (
                <div
                  onClick={() => handleSelect(allOption)}
                  className={`
                    cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                    ${selectedValue === allOption.value ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                    ${optionClassName}
                    ${showRadio ? 'flex items-center' : ''}
                  `}
                >
                  {showRadio && (
                    <input
                      type="radio"
                      checked={selectedValue === allOption.value}
                      readOnly
                      className="mr-2 accent-green-400"
                    />
                  )}
                  {allOption.label}
                </div>
              )}
              {/* Options */}
              {options.map((option, index) => {
                const optionValue = typeof option === 'object' ? option.value : option;
                const optionLabel = typeof option === 'object' ? option.label : option;
                const isSelected = selectedValue === optionValue;
                return (
                  <div
                    key={index}
                    onClick={() => handleSelect(option)}
                    className={`
                      cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-50 
                      ${isSelected ? 'text-green-600 bg-green-50' : 'text-gray-700'}
                      ${optionClassName}
                      ${showRadio ? 'flex items-center' : ''}
                    `}
                  >
                    {showRadio && (
                      <input
                        type="radio"
                        checked={isSelected}
                        readOnly
                        className="mr-2 accent-green-400"
                      />
                    )}
                    {optionLabel}
                  </div>
                );
              })}
              {/* Empty State */}
              {options.length === 0 && (
                <div className="px-3 py-1.5 text-xs text-gray-500 italic">
                  No options available
                </div>
              )}
            </div>
          )
      )}
    </div>
  );
};

export default DropdownCustom;
